import React from "react";

import * as Styled from "./styles";

import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemText,
  Typography,
} from "@mui/material";
import { ReturnButton } from "components/ReturnButton";
import { getDocuments } from "requests/Documentation";
import { getPdf } from "requests/File";
import { useAuth } from "shared/providers/auth";
import { useLocation, useParams } from "react-router-dom";
import { FaDownload } from "react-icons/fa";
import { MdClose } from "react-icons/md";

import { Image } from "react-grid-gallery";

import { GalleryDocuments } from "components/Gallery";
import toast from "react-hot-toast";
import { Document, Page, pdfjs } from "react-pdf";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { IFile } from "shared/types/RecordPhysical";

export interface CustomImage extends Image {
  original: string;
}

interface IDocumentFile extends IFile {}

interface ResponseData {
  [key: string]: string;
}

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * Caminho do arquivo, sem a query string.
 *
 * A documentacao passou a chegar como link temporario assinado
 * (`...arquivo.jpg?X-Amz-Signature=...`), entao testar a extensao na URL
 * inteira nunca casa. O tipo tem de ser decidido pelo path.
 */
const pathOf = (link: string): string => {
  try {
    return new URL(link).pathname.toLowerCase();
  } catch {
    return link.split("?")[0].toLowerCase();
  }
};

/** Nome do arquivo a partir da chave, sem a assinatura. */
const fileNameOf = (link: string): string =>
  decodeURIComponent(pathOf(link).split("/").pop() || "");

const Documentation: React.FC<{
  idPacientParam?: number;
  onClick?: () => void;
}> = ({ idPacientParam, onClick }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { idPacient } = useParams();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [objDoc, setObjDoc] = React.useState<string[]>([]);

  const [images, setImages] = React.useState<CustomImage[]>([]);
  const [pdfFiles, setPdfFiles] = React.useState<IDocumentFile[]>([]);
  const [selectedFile, setSelectedFile] = React.useState<IDocumentFile | null>(
    null
  );
  const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);
  const [pdfLoading, setPdfLoading] = React.useState<boolean>(false);
  const [pdfData, setPdfData] = React.useState<string | null>(null);
  const [pdfBlob, setPdfBlob] = React.useState<Blob | null>(null);
  const [pdfRemoteUrl, setPdfRemoteUrl] = React.useState<string | null>(null);
  const [numPages, setNumPages] = React.useState<number>(0);

  const isDocumentation = !location.pathname.includes("documentation");

  const transformDataToArray = (data: ResponseData): string[] => {
    return Object.keys(data)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => data[key]);
  };

  const documentsAll = React.useCallback(async () => {
    setLoading(true);

    try {
      setLoading(false);
      const { status, data } = await getDocuments({
        userId: user.data.id,
        idPac: Number(
          idPacientParam && idPacientParam > 0 ? idPacientParam : idPacient
        ),
      });

      if ([200, 201].includes(status)) {
        const transformedData: string[] = transformDataToArray(data.data);

        const imageExtensions = [
          ".jpg",
          ".jpeg",
          ".png",
          ".gif",
          ".bmp",
          ".svg",
        ];
        const imageLinks = transformedData.filter((link) =>
          imageExtensions.some((extension) => pathOf(link).endsWith(extension))
        );

        const customImages: CustomImage[] = imageLinks.map((link) => ({
          src: link,
          original: link,
          width: 320,
          height: 212,
          caption: "Orthodontic",
        }));

        setImages(customImages);

        const pdfLinks = transformedData.filter((link) =>
          pathOf(link).endsWith(".pdf")
        );

        const mappedPdfFiles: IDocumentFile[] = pdfLinks.map((link, index) => ({
          id: String(index),
          name: fileNameOf(link) || `documento-${index + 1}.pdf`,
          original_name: fileNameOf(link) || `documento-${index + 1}.pdf`,
          size: 0,
          size_formatted: "PDF",
          extension: "pdf",
          upload_date: "",
          upload_date_formatted: "",
          download_url: link,
          preview_url: null,
          type: "document",
          key: link,
          s3_key: "",
          bucket: "",
          view_url: link,
        }));

        setPdfFiles(mappedPdfFiles);
        setObjDoc(transformedData);
      } else {
        toast.error(data.message);
      }
    } catch (e: any) {
      setLoading(false);
    }
    setLoading(false);
  }, [idPacient, idPacientParam, user]);

  const fetchPdf = async (file: IDocumentFile) => {
    setIsDialogOpen(true);
    setPdfLoading(true);
    setPdfBlob(null);
    setPdfData(null);
    setPdfRemoteUrl(null);
    setSelectedFile(file);

    try {
      let data: Blob;

      try {
        const response = await getPdf({
          file,
          userId: user.data.id,
          idPac: Number(
            idPacientParam && idPacientParam > 0 ? idPacientParam : idPacient
          ),
        });

        data = response.data;
      } catch (error) {
        // Some documentation files are public S3 URLs without CORS headers.
        // In that case, fetch/blob is blocked by the browser, so fall back to
        // embedding the remote PDF URL directly in the modal.
        setPdfRemoteUrl(file.download_url);
        setPdfLoading(false);
        return;
      }

      if (!(data instanceof Blob)) {
        throw new Error("Arquivo inválido ou corrompido");
      }

      const url = URL.createObjectURL(data);
      setPdfBlob(data);
      setPdfData(url);
    } catch (error) {
      console.error("Erro ao carregar o PDF:", error);
      toast.error("Falha ao carregar o PDF.");
      setPdfData(null);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleCloseDialog = () => {
    if (pdfData && pdfBlob) {
      URL.revokeObjectURL(pdfData);
    }
    setIsDialogOpen(false);
    setPdfBlob(null);
    setPdfData(null);
    setPdfRemoteUrl(null);
    setSelectedFile(null);
    setNumPages(0);
  };

  const handleDownload = async (file: Blob, filename: string) => {
    try {
      const convertBlobToBase64 = (blob: Blob): Promise<string> =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

      const isNative = (window as any).ReactNativeWebView;

      if (isNative) {
        const base64String = await convertBlobToBase64(file);

        (window as any).ReactNativeWebView.postMessage(
          JSON.stringify({
            action: "downloadBase64File",
            value: base64String,
            filename,
          })
        );
      } else {
        const blobUrl = URL.createObjectURL(file);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      console.error("Erro ao baixar o PDF:", error);
      toast.error("Erro ao baixar o PDF.");
    }
  };

  React.useEffect(() => {
    documentsAll();
  }, [documentsAll]);

  return (
    <Container maxWidth="xl">
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <ReturnButton title="Documentação" onClick={onClick} />

          <Styled.ContainerPictures isDocumentation={isDocumentation}>
            {images.length > 0 ? <GalleryDocuments images={images} /> : ""}
            {pdfFiles.length > 0 ? (
              <List>
                {pdfFiles.map((file, index) => (
                  <Styled.StyledListItem
                    key={`${file.id}-${index}`}
                    secondaryAction={
                      <Styled.BoxButtons>
                        <Button variant="outlined" onClick={() => fetchPdf(file)}>
                          Visualizar
                        </Button>
                      </Styled.BoxButtons>
                    }
                  >
                    <ListItemText
                      primary={
                        file.original_name.length > 40
                          ? `${file.original_name.slice(0, 40)}...`
                          : file.original_name
                      }
                      secondary="PDF"
                    />
                  </Styled.StyledListItem>
                ))}
              </List>
            ) : objDoc.length < 1 ? (
              <Typography>Não existem documentos.</Typography>
            ) : (
              ""
            )}
          </Styled.ContainerPictures>

          <Dialog
            open={isDialogOpen}
            onClose={handleCloseDialog}
            maxWidth="lg"
            fullWidth
            scroll="paper"
          >
            <DialogTitle>
              <Styled.BoxTitle>
                {selectedFile &&
                selectedFile.original_name &&
                selectedFile.original_name.length > 30
                  ? `${selectedFile.original_name.slice(0, 30)}...`
                  : selectedFile?.original_name || "Documento"}

                <Button
                  variant="contained"
                  onClick={() => {
                    if (selectedFile && pdfBlob) {
                      handleDownload(pdfBlob, selectedFile.original_name);
                    } else if (selectedFile && pdfRemoteUrl) {
                      const isNative = (window as any).ReactNativeWebView;

                      if (isNative) {
                        (window as any).ReactNativeWebView.postMessage(
                          JSON.stringify({
                            action: "downloadFile",
                            value: pdfRemoteUrl,
                          })
                        );
                      } else {
                        const link = document.createElement("a");
                        link.href = pdfRemoteUrl;
                        link.target = "_blank";
                        link.rel = "noopener noreferrer";
                        link.download = selectedFile.original_name;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }
                    } else {
                      toast.error("Arquivo ainda não carregado.");
                    }
                  }}
                >
                  <FaDownload /> Baixar
                </Button>
              </Styled.BoxTitle>
              <IconButton
                aria-label="close"
                onClick={handleCloseDialog}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <MdClose />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Styled.PDFContainer>
                {!pdfLoading && !pdfData && (
                  <Box textAlign="center" p={4}>
                    <Typography color="error">
                      Falha ao carregar o PDF. Tente novamente.
                    </Typography>
                  </Box>
                )}

                {pdfLoading && <CircularProgress />}

                {pdfRemoteUrl && (
                  <Box sx={{ width: "100%", height: "80vh" }}>
                    <object
                      data={pdfRemoteUrl}
                      type="application/pdf"
                      width="100%"
                      height="100%"
                    >
                      <iframe
                        src={pdfRemoteUrl}
                        title={selectedFile?.original_name || "Documento PDF"}
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        onLoad={() => setPdfLoading(false)}
                      />
                    </object>
                  </Box>
                )}

                {pdfData && !pdfRemoteUrl && (
                  <TransformWrapper
                    initialScale={1}
                    minScale={0.6}
                    maxScale={4}
                    doubleClick={{ disabled: false }}
                    pinch={{ disabled: false }}
                    panning={{ disabled: false }}
                  >
                    <TransformComponent
                      wrapperStyle={{
                        height: "80vh",
                        overflow: "auto",
                      }}
                      contentStyle={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <Document
                        file={pdfData}
                        onLoadSuccess={({ numPages }) => {
                          setNumPages(numPages);
                          setPdfLoading(false);
                        }}
                        onLoadError={(err) => {
                          console.error("Erro ao renderizar o PDF", err);
                          setPdfLoading(false);
                          setPdfData(null);
                          toast.error("Erro ao renderizar o PDF.");
                        }}
                        loading={<CircularProgress />}
                      >
                        {Array.from({ length: numPages }, (_, index) => (
                          <Page key={`page_${index + 1}`} pageNumber={index + 1} />
                        ))}
                      </Document>
                    </TransformComponent>
                  </TransformWrapper>
                )}
              </Styled.PDFContainer>
            </DialogContent>
          </Dialog>
        </>
      )}
    </Container>
  );
};

export { Documentation };
